import { useEffect } from "react";
import { useAppStore, useCurrentMonth } from "./store/useAppStore";
import { useRecurringIncomeStore } from "./store/useRecurringIncomeStore";
import ReconcileButton from "./components/ReconcileButton";
import ReconciliationModal from "./components/ReconciliationModal";
import { useReconciliationStore } from "./store/useReconciliationStore";
import HeroSection from "./components/hero/HeroSection";
import DailyHeatmap from "./components/heatmap/DailyHeatmap";
import TransactionsCard from "./components/transactions/TransactionsCard";
import CategoryBreakdown from "./components/breakdown/CategoryBreakdown";
import RecurringIncomeCard from "./components/recurring-income/RecurringIncomeCard";
import BudgetWidget from "./components/budget/BudgetWidget";
import RecurringExpenseSummaryWidget from "./components/RecurringExpenseSummaryWidget";
import MonthlySummarySkeleton from "./components/MonthlySummarySkeleton";
import SuggestionsSection from "./components/SuggestionsSection";
import { FeatureGate } from "../feature-flags/components/FeatureGate";
import { useFeatureFlag } from "../feature-flags/store/useFeatureFlagStore";
import { FeatureFlag } from "../feature-flags/types/featureFlags.types";
import { refreshSuggestions } from "@/features/accounts/store/useAccountStore";
import { useRecurringExpenseStore } from "../recurring-expenses/store/useRecurringExpenseStore";

export default function MonthlySummaryPage() {
  const {
    viewYear,
    viewMonth,
    loadMonth,
    summaryError,
    loadPendingReconciliationCount,
  } = useAppStore();
  const currentMonth = useCurrentMonth();

  useEffect(() => {
    loadMonth(viewYear, viewMonth);
    loadPendingReconciliationCount();
    useRecurringIncomeStore.getState().loadItems();
    void refreshSuggestions();
  }, []);

  const recurringEnabled = useFeatureFlag(FeatureFlag.Recurring);
  useEffect(() => {
    if (recurringEnabled) useRecurringExpenseStore.getState().loadSummary();
  }, [recurringEnabled]);

  const openReconciliation = useReconciliationStore((s) => s.open);

  return (
    <>
      <ReconcileButton onClick={openReconciliation} />
      <ReconciliationModal />

      {summaryError && (
        <div className="mb-4 px-4 py-3 rounded-[10px] bg-red-500/10 text-red-600 dark:text-red-400 text-sm">
          {summaryError}
        </div>
      )}

      {!currentMonth && !summaryError ? (
        <MonthlySummarySkeleton />
      ) : currentMonth ? (
        <>
          {/* Recurring income leads on desktop; on mobile the hero comes first */}
          <div className="flex flex-col gap-4 sm:gap-5">
            {/* Hero */}
            <HeroSection data={currentMonth} />

            {/* Budget widget */}
            <FeatureGate flag={FeatureFlag.Budget}>
              <section>
                <BudgetWidget
                  year={currentMonth.year}
                  month={currentMonth.monthIndex + 1}
                />
              </section>
            </FeatureGate>

            {/* Recurring expense summary */}
            <FeatureGate flag={FeatureFlag.Recurring}>
              <section>
                <RecurringExpenseSummaryWidget />
              </section>
            </FeatureGate>

            {/* Recurring Income */}
            <section className="sm:order-first">
              <RecurringIncomeCard />
            </section>

            {/* Recurring expense suggestions */}
            <FeatureGate flag={FeatureFlag.Recurring}>
              <section className="empty:hidden">
                <SuggestionsSection />
              </section>
            </FeatureGate>

            {/* Category breakdown + Heatmap */}
            <section className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr] gap-4 sm:gap-5">
              <CategoryBreakdown
                categories={currentMonth.categories}
                total={currentMonth.categories.reduce(
                  (s, c) => s + c.amount,
                  0,
                )}
              />
              <DailyHeatmap
                heatmap={currentMonth.heatmap}
                month={currentMonth.monthIndex}
                year={currentMonth.year}
              />
            </section>

            {/* Transactions */}
            <section>
              <TransactionsCard />
            </section>
          </div>

          {/* Footer */}
          <footer className="text-center text-[11px] tracking-[0.04em] mt-10 pt-8 text-muted-foreground border-t border-border">
            BOO · your money, quietly organized · {currentMonth.month}
          </footer>
        </>
      ) : null}
    </>
  );
}
